import { CircularProgress, debounce } from '@material-ui/core';
import { getConfirmedCheckoutSessionByMerchantReference1, NotFoundException } from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useSetIsDirtyListener } from '@/components/modules/Application/hooks/useSetDirtyListener';
import { Icon } from '@/components/shared/components/Icon';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const useCheckReference = ({
  referenceName = 'orderReference1',
  enabled = true,
}: {
  referenceName?: string;
  enabled?: boolean;
}) => {
  const { palette } = useCustomTheme();

  const {
    state: { orderReference1 },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const { watch, formState } = useFormContext();
  useSetIsDirtyListener(formState.isDirty);

  const referenceWatcher = watch(referenceName);

  const [checking, setChecking] = useState(false);
  const [isReferenceTaken, setIsReferenceTaken] = useState(false);

  const checkAndUpdateReference = async (value: string) => {
    setIsReferenceTaken(false);

    if (!value) {
      updateApplicationContext({
        orderReference1: undefined,
      });
      return;
    }

    let orderReference1: string | undefined = value;
    try {
      await getConfirmedCheckoutSessionByMerchantReference1({ merchantReference1: orderReference1 });
      orderReference1 = undefined;
      setIsReferenceTaken(true);
    } catch (error) {
      if (error instanceof NotFoundException) {
        orderReference1 = value;
      }
    } finally {
      updateApplicationContext({ orderReference1 });
      setChecking(false);
    }
  };

  const referenceAvailable = checking ? (
    <CircularProgress color={'secondary'} size={16} style={{ marginBottom: '-4px', paddingInline: '3px' }} />
  ) : orderReference1 ? (
    <Icon style={{ marginBottom: '-4px' }} size={20} color={palette.success.main}>
      check_circle
    </Icon>
  ) : null;

  const checkAndUpdateReferenceDebounced = useRef(debounce(checkAndUpdateReference, 1000));

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!referenceWatcher) {
      updateApplicationContext({
        orderReference1: undefined,
      });
      setChecking(false);
    } else {
      setChecking(true);
    }
    checkAndUpdateReferenceDebounced.current(referenceWatcher);
  }, [referenceWatcher, enabled]);

  return { referenceAvailable, isReferenceTaken: enabled && !checking && !!referenceWatcher && isReferenceTaken };
};
